// src/pages/TermsOfService.jsx
import React from 'react';
import './PrivacyPolicy.css'; // Reusing the same CSS for consistency

const TermsOfService = () => (
  <div className="privacy-container">
    <h1>Terms of Service</h1>
    <p><strong>Effective Date:</strong> April 17, 2026</p>

    <p>
      These Terms of Service ("Terms") constitute a binding agreement between PanelZero, Inc. 
      ("PanelZero," "we," or "us") and you or the entity you represent ("Customer" or "you"). 
    </p>
    <p>
      By accessing or using our Enterprise Software platform (the "Service"), you agree to be bound 
      by these Terms. If you are entering into these Terms on behalf of a company, healthcare organization, or other legal entity, you represent that you have the authority to bind that entity.
    </p>

    <section>
      <h2>1. Provision of Service</h2>
      <p>
        <strong>1.1 Access Rights:</strong> Subject to payment of applicable fees, PanelZero grants 
        Customer a non-exclusive, non-transferable right to access and use the Service for 
        internal business purposes during the Subscription Term.
      </p>
      <p>
        <strong>1.2 Authorized Users:</strong> Customer is responsible for all actions taken by 
        its employees, contractors, or agents ("Authorized Users") who access the Service using 
        Customer’s account credentials.
      </p>
      <p>
        <strong>1.3 Restrictions:</strong> Customer shall not: (a) reverse engineer the Service; 
        (b) use the Service to build a competitive product; or (c) use the Service for unlawful purposes.
      </p>
    </section>

    <section>
      <h2>2. Data Rights and Ownership</h2>
      <p>
        <strong>2.1 Customer Data:</strong> Customer retains all ownership rights to the data, 
        content, and files uploaded to the Service ("Customer Data"). You grant PanelZero a 
        limited license to process this data solely to provide the Service.
      </p>
      <p>
        <strong>2.2 PanelZero IP:</strong> PanelZero retains all rights, title, and interest in 
        the Service, including source code, AI models, algorithms, and interface designs.
      </p>
      <p>
        <strong>2.3 Aggregated Data:</strong> Customer agrees that PanelZero may use anonymized 
        and aggregated data derived from the Service for product improvement and analytics, 
        provided that such data does not identify the Customer or any individual.
      </p>
    </section>

    <section>
      <h2>3. Fees and Payment</h2>
      <p>
        <strong>3.1 Subscription Fees:</strong> Services are billed in advance on a subscription basis 
        (e.g., monthly or annually) as specified in your Order Form.
      </p>
      <p>
        <strong>3.2 Late Payments:</strong> Unpaid invoices may result in the suspension of access 
        to the Service until payment is received.
      </p>
    </section>

    <section>
      <h2>4. AI Accuracy Disclaimer</h2>
      <p>
        The Service utilizes Artificial Intelligence (AI) to generate summaries, transcripts, and insights. 
        <strong>AI is not infallible.</strong> PanelZero does not warrant that AI-generated content 
        will be error-free or 100% accurate. Customer is responsible for verifying critical information 
        against original source materials before taking action based on AI outputs.
      </p>
    </section>

    <section>
      <h2>5. Confidentiality</h2>
      <p>
        Each party agrees to protect the other party’s Confidential Information (including business data, 
        pricing, and technical specifications) with the same degree of care it uses to protect its own 
        confidential data, but no less than reasonable care.
      </p>
    </section>

    <section>
      <h2>6. Warranties and Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED "AS IS." PANELZERO DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, 
        INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT 
        GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
      </p>
    </section>

    <section>
      <h2>7. Limitation of Liability</h2>
      <p>
        IN NO EVENT SHALL PANELZERO BE LIABLE FOR ANY INDIRECT, CONSEQUENTIAL, OR PUNITIVE DAMAGES 
        (INCLUDING LOSS OF DATA OR PROFITS). OUR TOTAL LIABILITY ARISING OUT OF THESE TERMS SHALL NOT 
        EXCEED THE TOTAL AMOUNT PAID BY CUSTOMER IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
      </p>
    </section>

    <section>
      <h2>8. Term and Termination</h2>
      <p>
        <strong>8.1 Term:</strong> These Terms remain in effect as long as you have an active subscription.
      </p>
      <p>
        <strong>8.2 Termination:</strong> Either party may terminate this agreement if the other party 
        materially breaches these Terms and fails to cure such breach within 30 days of notice.
      </p>
      <p>
        <strong>8.3 Effect of Termination:</strong> Upon termination, your right to use the Service ceases. 
        Customer may request export of Customer Data within 30 days of termination.
      </p>
    </section>

    <section>
      <h2>9. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of Texas. Any disputes regarding these 
        Terms shall be resolved exclusively in the state or federal courts located in Bexar County, Texas.
      </p>
    </section>

    <section>
      <h2>10. Updates to Terms</h2>
      <p>
        We may update these Terms periodically. If changes are material, we will notify you via email 
        or an in-app notification. Continued use of the Service after the effective date constitutes 
        acceptance of the new Terms.
      </p>
    </section>

    {/* Restore when legal@panelzero.com is live:
    <section>
      <h2>Contact Us</h2>
      <p>
        For legal inquiries regarding these Terms, please contact us at <a href="mailto:legal@panelzero.com">legal@panelzero.com</a>.
      </p>
    </section>
    */}
  </div>
);

export default TermsOfService;